/**
 * PlacementControls — the in-context placement input panel (shown on the right,
 * both on the Grid drawer and the Canvas). Owns rotation / empty-area opacity /
 * background / confirm; the artwork itself is dragged on the real surface. Live
 * values come from the shared placementEditStore via usePlacementEdit.
 */
import { usePlacementEdit } from "./usePlacementEdit";

export function PlacementControls({
  artworkId, onCommitted, onClose, title = "Edit Placement",
}: { artworkId: string; onCommitted?: () => void; onClose?: () => void; title?: string }) {
  const e = usePlacementEdit(artworkId, onCommitted, onClose);
  const s = e.state;

  return (
    <div data-testid="placement-controls" className="flex flex-col gap-3 rounded-xl border border-teal-300/25 bg-[#0b0f16]/95 p-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        <button data-testid="placement-cancel-btn" onClick={e.cancel}
          className="rounded-full border border-white/15 px-3 py-1 text-xs text-white/70 hover:text-white">Cancel</button>
      </div>

      {!s ? (
        <div className="py-6 text-center text-xs text-white/40">Loading…</div>
      ) : (
        <>
          <div data-testid="placement-position-readout" className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
            <span className="text-xs text-white/50">Position</span>
            <span className="font-mono text-sm text-white">({s.ax}, {s.ay})</span>
          </div>
          <p className="-mt-1 text-[10px] text-teal-200/80">Drag the artwork directly on the surface to move it.</p>

          <label className="text-xs text-white/50">Rotation · {s.rotDeg}°
            <input data-testid="placement-rotation-input" type="range" min={0} max={359} value={s.rotDeg}
              onChange={(ev) => e.setRot(Number(ev.target.value))} className="mt-1 w-full" />
          </label>

          <div className="flex items-center gap-3">
            <label className="text-xs text-white/50">Background</label>
            <input data-testid="placement-bg-input" type="color" value={s.bg ?? "#000000"}
              onChange={(ev) => e.setBg(ev.target.value)} className="h-7 w-10 rounded bg-transparent" />
            <button data-testid="placement-bg-transparent-btn" onClick={() => e.setBg(null)}
              className={`rounded-full border px-3 py-1 text-xs ${s.bg === null ? "border-teal-300/60 text-teal-200" : "border-white/15 text-white/60"}`}>
              Transparent
            </button>
          </div>

          <label className="text-xs text-white/50">Empty-area opacity · {s.opacityPct}%
            <input data-testid="placement-opacity-input" type="range" min={0} max={100} value={s.opacityPct}
              onChange={(ev) => e.setOpacity(Number(ev.target.value))} className="mt-1 w-full" />
          </label>

          <div data-testid="placement-status" className={`rounded-lg border p-2.5 text-sm ${e.valid ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-200" : "border-red-400/30 bg-red-400/5 text-red-200"}`}>
            <span data-testid="placement-validity" className="font-semibold">
              {e.valid ? "VALID PLACEMENT" : "INVALID PLACEMENT"}
            </span>
            {e.preview && <span className="ml-2 font-mono text-[11px] text-white/50">{e.preview.reserved_cells} cells</span>}
            {!e.valid && e.reasons.length > 0 && (
              <ul data-testid="placement-reasons" className="mt-1 list-disc pl-4 text-xs">
                {e.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            )}
          </div>

          {e.error && <div data-testid="placement-error" className="text-sm text-red-300">{e.error}</div>}

          <div className="flex justify-end gap-2">
            <button data-testid="placement-cancel-btn-2" onClick={e.cancel}
              className="rounded-full border border-white/15 px-4 py-2 text-sm text-white/70">Cancel</button>
            <button data-testid="placement-confirm-btn" onClick={e.commit} disabled={!e.valid || e.committing}
              className="rounded-full bg-teal-400 px-4 py-2 text-sm font-semibold text-black disabled:cursor-not-allowed disabled:opacity-40">
              {e.committing ? "Committing…" : "Confirm"}
            </button>
          </div>
          <p className="text-[10px] text-white/35">Content, hash and dimensions never change — only placement. Backend is the sole authority.</p>
        </>
      )}
    </div>
  );
}
